import { create } from 'zustand';
import { db } from '@/lib/db/db';
import type { Account, Profile } from '@/types';
import { supabase } from '@/lib/supabase';

interface AuthState {
    account: Account | null;
    profile: Profile | null;
    isGuest: boolean;
    loading: boolean;
    isParent: boolean;
    isCoach: boolean;

    // Actions
    login: (email: string, pin: string) => Promise<{ success: boolean; error?: string }>;
    signup: (email: string, pin: string, role: 'athlete' | 'parent' | 'coach') => Promise<{ success: boolean; error?: string }>;
    loginWithGoogle: (role?: 'athlete' | 'parent' | 'coach') => Promise<{ success: boolean; error?: string }>;
    logout: () => void;
    enterGuestMode: () => void;
    hydrate: () => Promise<void>;
    manuallyHydrate: (url: string) => Promise<{ success: boolean; error?: string }>;
}

export const useAuthStore = create<AuthState>((set) => ({
    account: null,
    profile: null,
    isGuest: false,
    loading: true,
    isParent: false,
    isCoach: false,

    login: async (email, pin) => {
        // We will treat the pin as the password. Supabase requires min 6 chars.
        const password = pin.length < 6 ? pin.padEnd(6, '0') : pin;

        const { error } = await supabase.auth.signInWithPassword({
            email,
            password
        });

        if (error) {
            // Legacy Local Login (Preserved for existing test users offline)
            const account = await db.accounts.where('email').equals(email).first();
            if (!account || account.passwordHash !== pin) {
                return { success: false, error: error.message || 'Invalid credentials' };
            }
            set({
                account,
                isGuest: false,
                isParent: account.role === 'parent',
                isCoach: account.role === 'coach'
            });
            localStorage.setItem('palaestra_auth_id', account.id.toString());
            return { success: true };
        }

        return { success: true };
    },

    loginWithGoogle: async (_role = 'athlete') => {
        // ENSURE we never redirect to localhost on production
        const redirectUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
            ? window.location.origin
            : 'https://palaestra.rx3.us';

        console.log('Initiating Google Login. Forcing Redirect to:', redirectUrl);

        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: redirectUrl,
                queryParams: {
                    access_type: 'offline',
                    prompt: 'consent',
                },
            },
        });

        if (error) {
            console.error('Google Auth Error:', error);
            return { success: false, error: error.message };
        }
        return { success: true };
    },

    signup: async (email, pin, role) => {
        const password = pin.length < 6 ? pin.padEnd(6, '0') : pin;

        // Sign up with Supabase
        const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: { role } // Store role in user metadata
            }
        });

        if (error) {
            return { success: false, error: error.message };
        }

        // The exact account & athlete generation will happen on first successful login 
        // after they verify their email, or immediately if email verification is off, 
        // handled in the hydrate() listener below. For now, we seed a fallback just in case.

        const existing = await db.accounts.where('email').equals(email).first();
        if (!existing) {
            const id = await db.accounts.add({
                email,
                passwordHash: 'supabase-oauth', // Masked local password
                role,
                isGuest: false,
                createdAt: new Date().toISOString()
            } as Account);

            const athleteId = `ath-${id}-${Math.floor(Math.random() * 1000)}`;
            const name = email.split('@')[0] || 'User';

            await db.athletes.add({
                id: athleteId,
                accountId: id,
                name: name,
                level: 'Beginner',
                experienceYears: 0,
                avatar: role === 'athlete' ? '🤼' : role === 'coach' ? '📋' : '🏠',
                createdAt: new Date().toISOString()
            });

            await db.appSettings.update(1, {
                activeAthleteId: athleteId,
                onboardingCompleted: false
            });
        }

        return { success: true };
    },

    logout: async () => {
        await supabase.auth.signOut();
        set({ account: null, profile: null, isGuest: false, isParent: false, isCoach: false });
        localStorage.removeItem('palaestra_auth_id');
    },

    enterGuestMode: () => {
        set({ isGuest: true, account: null, profile: null, isParent: false, isCoach: false });
    },

    hydrate: async () => {
        // 1. Check Supabase Session
        const { data: { session: initialSession } } = await supabase.auth.getSession();

        const processSession = async (session: any) => {
            if (session?.user) {
                // Fetch Supabase Profile
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', session.user.id)
                    .single();

                // Sync Supabase User -> Local Dexie Account
                const email = session.user.email!;
                let account = await db.accounts.where('email').equals(email).first();

                if (!account) {
                    const role = session.user.user_metadata.role || 'athlete';
                    const id = await db.accounts.add({
                        email,
                        passwordHash: 'supabase-oauth',
                        role,
                        isGuest: false,
                        createdAt: new Date().toISOString()
                    } as Account);
                    account = await db.accounts.get(id);

                    // Create athlete for this account
                    const athleteId = `ath-${id}-${Math.floor(Math.random() * 1000)}`;
                    const name = session.user.user_metadata.full_name || session.user.user_metadata.name || email.split('@')[0] || 'Athlete';
                    await db.athletes.add({
                        id: athleteId,
                        accountId: id,
                        name: name,
                        level: 'Beginner',
                        experienceYears: 0,
                        avatar: role === 'athlete' ? '🤼' : role === 'coach' ? '📋' : '🏠',
                        createdAt: new Date().toISOString()
                    });

                    // If returning user (onboarding already done on another device/session),
                    // auto-create a completed onboarding profile so they skip the wizard
                    if (session.user.user_metadata.onboarding_completed) {
                        console.log('Restoring onboarding completion from Supabase metadata');
                        await db.onboardingProfiles.put({
                            athleteId,
                            role: role as any,
                            yearsBand: '0-1',
                            level: 'youth',
                            goals: [],
                            daysPerWeek: 3,
                            sessionMinutes: 20,
                            completedAt: new Date().toISOString(),
                        } as any);
                    }

                    // Set this athlete as active
                    await db.appSettings.update(1, { activeAthleteId: athleteId });
                }

                set({
                    account,
                    profile,
                    loading: false,
                    isGuest: false,
                    isParent: account?.role === 'parent',
                    isCoach: account?.role === 'coach'
                });
            } else {
                // Fallback to Local ID (Legacy)
                const id = localStorage.getItem('palaestra_auth_id');
                if (id) {
                    const account = await db.accounts.get(parseInt(id));
                    if (account) {
                        set({
                            account,
                            loading: false,
                            isParent: account.role === 'parent',
                            isCoach: account.role === 'coach'
                        });
                        return;
                    }
                }
                set({ account: null, loading: false, isParent: false, isCoach: false });
            }
        };

        // Initial process
        await processSession(initialSession);

        // Listen for Auth Changes (Only once)
        supabase.auth.onAuthStateChange(async (_event, session) => {
            await processSession(session);
        });
    },

    manuallyHydrate: async (url: string) => {
        try {
            console.log('Manually hydrating from URL...');
            // Extract hash/fragment
            const fragment = url.includes('#') ? url.split('#')[1] : (url.includes('?') ? url.split('?')[1] : url);
            const params = new URLSearchParams(fragment);

            const accessToken = params.get('access_token');
            const refreshToken = params.get('refresh_token');

            if (!accessToken || !refreshToken) {
                return { success: false, error: 'No tokens found in URL' };
            }

            const { data, error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
            });

            if (error) throw error;

            console.log('Manual session set successfully for:', data.user?.email);
            return { success: true };
        } catch (e: any) {
            console.error('Manual hydration failed:', e);
            return { success: false, error: e.message };
        }
    }
}));
